import {useQuery} from "@apollo/client";

import {ChecksModalTable} from "@/components/ChecksModalButton/subComponents/ChecksModalTable.tsx";
import {FormModal} from "@/components/form/overlay/FormModal.tsx";
import {useTable} from "@/lib/hooks/useTable.ts";
import {ChecksQueryModelType, GQL_CHECKS_QUERY2} from "@/lib/queries/checks.ts";
import {GQL_MAINTENANCE_AUDIT_EVENT_CHECKS, MaintenanceAuditEventChecksPageResult} from "@/lib/queries/maintenance.ts";
import {ServiceList} from "@/lib/types/checks.ts";
import {ChecksControlBar} from "@/pages/checks/list/components/ChecksControlBar.tsx";

const DEFAULT_LIMIT = 5;
const DEFAULT_SORT = "name";

type ChecksModalModelProps = {
  id: number;
  model_type: ChecksQueryModelType;
  serviceIds?: never;
  auditEventId?: never;
};

type ChecksModalServiceIdsProps = {
  serviceIds: number[];
  id?: never;
  model_type?: never;
  auditEventId?: never;
};

type ChecksModalAuditEventProps = {
  auditEventId: number;
  id?: never;
  model_type?: never;
  serviceIds?: never;
};

export type ChecksModalVariantProps = ChecksModalModelProps | ChecksModalServiceIdsProps | ChecksModalAuditEventProps;

type ChecksModalProps = ChecksModalVariantProps & {
  show: boolean;
  hide: () => void;
  title?: string;
};

export const ChecksModal = ({show, hide, title = "Checks", ...rest}: ChecksModalProps) => {
  const {
    search,
    searchDebounced,
    searchChanged,
    pagination,
    setPagination,
    sorting,
    sortingString,
    setSorting,
    filterSets,
    setFilterSets,
  } = useTable({
    resetPaginationOnFilter: true,
    resetPaginationOnSearch: true,
    useUrlParams: false,
    defaultLimit: DEFAULT_LIMIT,
    defaultSort: DEFAULT_SORT,
  });

  const isAuditMode = "auditEventId" in rest;

  const queryVariables =
    "serviceIds" in rest && rest.serviceIds
      ? {service_ids: rest.serviceIds}
      : {model_id: rest.id, model_type: rest.model_type};

  const {data, loading, previousData} = useQuery<ServiceList>(GQL_CHECKS_QUERY2, {
    variables: {
      ...queryVariables,
      ...pagination,
      search: searchDebounced,
      sort: sortingString,
      filters: filterSets,
    },
    skip: !show || isAuditMode,
  });

  const {
    data: auditData,
    loading: auditLoading,
    previousData: auditPreviousData,
  } = useQuery<MaintenanceAuditEventChecksPageResult>(GQL_MAINTENANCE_AUDIT_EVENT_CHECKS, {
    variables: {
      audit_event_id: isAuditMode ? rest.auditEventId : 0,
      ...pagination,
      search: searchDebounced,
      sort: sortingString,
    },
    skip: !show || !isAuditMode,
  });

  const renderData = loading ? previousData : data;
  const auditRenderData = auditLoading ? auditPreviousData : auditData;

  const modelType = "model_type" in rest ? rest.model_type : undefined;
  const modelId = "id" in rest ? rest.id : undefined;

  return (
    <FormModal
      title={title}
      cancelBtnText={"Close"}
      hasSubmitButton={false}
      onCancel={hide}
      show={show}
      size="lg"
      formId={"model-checks-form"}
    >
      <>
        <ChecksControlBar
          search={search}
          setSearch={searchChanged}
          filterSets={filterSets}
          onFilterSetChange={setFilterSets}
          portal={true}
          disabled={isAuditMode}
          disabledTooltip="Filtering is disabled as this list is a historical snapshot that may include deleted checks"
        />
        <ChecksModalTable
          data={isAuditMode ? undefined : renderData}
          auditChecks={isAuditMode ? auditRenderData?.maintenanceAuditEventChecks : undefined}
          sorting={sorting}
          onSortingChange={setSorting}
          pagination={pagination}
          updatePagination={setPagination}
          modelType={modelType}
          modelId={modelId}
        />
      </>
    </FormModal>
  );
};
